import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { CheckIcon, XIcon, ArrowRightIcon, SparklesIcon } from 'lucide-react';
const features = [{
  name: 'Close CRM Setup & Configuration',
  consulting: true,
  setup: true,
  managed: true
}, {
  name: 'Pipeline Design',
  consulting: '1 Pipeline',
  setup: '3 Pipelines',
  managed: 'Unlimited'
}, {
  name: 'Data Migration',
  consulting: 'Basic Consulting',
  setup: 'Up to 10,000 Contacts',
  managed: 'Ongoing Maintenance'
}, {
  name: 'Email Integration',
  consulting: true,
  setup: true,
  managed: true
}, {
  name: 'Automations',
  consulting: 'Basic',
  setup: 'Advanced',
  managed: 'Ongoing Optimization'
}, {
  name: 'Telephony Setup',
  consulting: false,
  setup: true,
  managed: true
}, {
  name: 'API Integrations',
  consulting: false,
  setup: true,
  managed: 'Maintenance & Updates'
}, {
  name: 'Custom Reporting',
  consulting: false,
  setup: true,
  managed: 'Monthly Reports'
}, {
  name: 'Team Training',
  consulting: 'Video Tutorials',
  setup: '4 Hours Live',
  managed: 'Ongoing Training'
}, {
  name: 'Support',
  consulting: 'Email',
  setup: '30 Days Priority',
  managed: 'Permanent Priority'
}, {
  name: 'Monthly Optimization Calls',
  consulting: false,
  setup: false,
  managed: true
}, {
  name: 'Dedicated Account Manager',
  consulting: false,
  setup: false,
  managed: true
}];
const packages = [{
  name: 'Close CRM Consulting',
  price: '',
  period: '',
  description: 'Strategic consulting and basic setup for the start',
  cta: 'Request Pricing',
  highlighted: false,
  key: 'consulting'
}, {
  name: 'Close CRM Set Up',
  price: '',
  period: '',
  description: 'Complete implementation for professional teams',
  cta: 'Request Pricing',
  highlighted: true,
  key: 'setup'
}, {
  name: 'Managed Service',
  price: '',
  period: '',
  description: 'Ongoing management, optimization, and support',
  cta: 'Request Pricing',
  highlighted: false,
  key: 'managed'
}];
export function PricingSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const renderFeatureValue = (value: boolean | string) => {
    if (value === true) {
      return <CheckIcon className="w-5 h-5 text-emerald-400 mx-auto" data-id="element-146" data-mp="3i08mf0ysl0p" />;
    }
    if (value === false) {
      return <XIcon className="w-5 h-5 text-gray-600 mx-auto" data-id="element-147" data-mp="3kyi69xfzkcyv" />;
    }
    return <span className="text-gray-300 text-sm" data-id="element-148" data-mp="3l0bbd8ayap4r">{value}</span>;
  };
  return <section className="py-24 lg:py-32 bg-[#0c0c0c]" id="pricing" ref={ref} data-id="element-149" data-mp="3pymjnellkrkv">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-150" data-mp="3bxa95ip7hi6d">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center max-w-3xl mx-auto mb-16" data-id="element-151" data-mp="3c065j2lenysp">
          <span className="inline-block px-4 py-1.5 rounded-full bg-[#0ea5e9]/10 text-[#0ea5e9] text-sm font-medium mb-6" data-id="element-152" data-mp="369otwo5uxqg2">
            Pricing
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight" data-id="element-153" data-mp="3g8xljad92sz9">
            Transparent packages for every phase
          </h2>
          <p className="mt-6 text-lg text-gray-400" data-id="element-154" data-mp="3p29gjnuyqzbf">
            Choose between a one-time setup or ongoing support. Close CRM
            license costs are separate.
          </p>
        </motion.div>

        {/* Package cards */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="grid lg:grid-cols-3 gap-6 mb-12" data-id="element-155" data-mp="2u9j3kpnnctv">
          {packages.map((pkg, index) => <div key={index} className={`relative p-8 rounded-2xl flex flex-col ${pkg.highlighted ? 'bg-gradient-to-b from-[#0ea5e9]/20 to-[#1a1a1a] border-2 border-[#0ea5e9]/50' : 'bg-[#1a1a1a] border border-white/5'}`} data-id="element-156" data-mp="qsqt0uj55srp" data-mpi={index}>
              {pkg.highlighted && <div className="absolute -top-4 left-1/2 -translate-x-1/2" data-id="element-157" data-mp="32mx79c72xb9">
                  <span className="inline-flex items-center gap-1 px-4 py-1.5 rounded-full bg-[#0ea5e9] text-white text-sm font-semibold shadow-lg shadow-[#0ea5e9]/20" data-id="element-158" data-mp="7yligo287z7g">
                    <SparklesIcon className="w-4 h-4" data-id="element-159" data-mp="1je0gzwa32z4o" />
                    Recommended
                  </span>
                </div>}
              <div className="mb-auto" data-id="element-160" data-mp="1ofzjz51wvu13">
                <h3 className="text-xl font-bold text-white mb-2" data-id="element-161" data-mp="38wgw3h8vsxu9">
                  {pkg.name}
                </h3>
                <p className="text-gray-400 text-sm mb-6 min-h-[40px]" data-id="element-162" data-mp="33yf94l8c4edi">
                  {pkg.description}
                </p>
                <div className="mb-8" data-id="element-163" data-mp="2z4b1dzr8w2bd">
                  <span className="text-4xl font-bold text-white" data-id="element-164" data-mp="2u7ufzdbfk21z">
                    {pkg.price}
                  </span>
                  <span className="text-gray-500 text-sm ml-1" data-id="element-165" data-mp="2q6h8kysovqsy">
                    {pkg.period}
                  </span>
                </div>
              </div>
              <a href="/free-strategy-call" className={`group w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all duration-200 ${pkg.highlighted ? 'bg-[#0ea5e9] text-white hover:bg-[#0284c7] shadow-lg shadow-[#0ea5e9]/25' : 'bg-white/5 text-white border border-white/10 hover:bg-white/10'}`} data-id="element-166" data-mp="2g79u28a7xq49">
                {pkg.cta}
                <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-167" data-mp="23x0nr9slf9cp" />
              </a>
            </div>)}
        </motion.div>

        {/* Feature comparison table */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.4
      }} className="rounded-2xl bg-[#1a1a1a] border border-white/5 overflow-hidden" data-id="element-168" data-mp="1u05qeu823f00">
          <div className="overflow-x-auto" data-id="element-169" data-mp="2dp6iuse566w4">
            <table className="w-full min-w-[800px]" data-id="element-170" data-mp="2hnxeev2ylgh4">
              <thead data-id="element-171" data-mp="2hqrwwh2ewr7w">
                <tr className="border-b border-white/5" data-id="element-172" data-mp="1286pdtg25m0i">
                  <th className="text-left p-6 text-gray-400 font-medium w-1/4" data-id="element-173" data-mp="126n75xaud0w3">
                    Features
                  </th>
                  <th className="p-6 text-center text-white font-semibold w-1/4" data-id="element-174" data-mp="yq0twh61h9a1">
                    Consulting
                  </th>
                  <th className="p-6 text-center text-[#0ea5e9] font-semibold bg-[#0ea5e9]/5 w-1/4" data-id="element-175" data-mp="tpwder54sejt">
                    CRM Set Up
                  </th>
                  <th className="p-6 text-center text-white font-semibold w-1/4" data-id="element-176" data-mp="18ludgki6e31b">
                    Managed Service
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-177" data-mp="fxf3wdhtccxd">
                {features.map((feature, index) => <tr key={index} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02] transition-colors" data-id="element-178" data-mp="b1zrlpzgep32" data-mpi={index}>
                    <td className="p-4 pl-6 text-gray-300 text-sm font-medium" data-id="element-179" data-mp="kxkx3y3qe7hk">
                      {feature.name}
                    </td>
                    <td className="p-4 text-center border-r border-white/5 last:border-0" data-id="element-180" data-mp="3tcbgvoe26mas">
                      {renderFeatureValue(feature.consulting)}
                    </td>
                    <td className="p-4 text-center bg-[#0ea5e9]/5 border-r border-white/5 last:border-0" data-id="element-181" data-mp="32xkwzsj3vp29">
                      {renderFeatureValue(feature.setup)}
                    </td>
                    <td className="p-4 text-center" data-id="element-182" data-mp="3ctebhhgdxsmo">
                      {renderFeatureValue(feature.managed)}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Guarantee */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.6
      }} className="mt-12 p-8 rounded-2xl bg-gradient-to-r from-emerald-500/10 to-[#0ea5e9]/10 border border-emerald-500/20 text-center max-w-2xl mx-auto" data-id="element-183" data-mp="3qsxa9jnb3rmb">
          <h3 className="text-xl font-bold text-white mb-2" data-id="element-184" data-mp="1xxwbds2s8t8z">
            30-Day Money-Back Guarantee
          </h3>
          <p className="text-gray-400" data-id="element-185" data-mp="1swp3lt0n3u2b">
            Applies to the Consulting and CRM Set Up package. If you are not
            satisfied, you will get your money back.
          </p>
        </motion.div>
      </div>
    </section>;
}